import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCcw, Search, Shirt, Filter as FilterIcon, Calendar as CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import { useToast } from '@/components/ui/use-toast';
import { fetchSolicitudesPrendas, updateSolicitudPrendaEstado } from '@/services/solicitudes.service';
import DetalleSolicitudModal from '@/components/solicitudes/DetalleSolicitudModal';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_LIMIT = 200;

const ExtraRutasPage = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  
  // Estados para filtros
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  
  // Obtener el primer y último día del mes actual
  const getDefaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      fechaInicio: firstDay.toISOString().split('T')[0],
      fechaFin: lastDay.toISOString().split('T')[0]
    };
  };
  
  const [filters, setFilters] = useState(getDefaultDateRange());

  const loadSolicitudes = useCallback(async () => {
    setIsLoading(true);
    try {
      // Construir query con filtros de fecha
      const query = { estado: 'No aprobada' };
      
      if (filters.fechaInicio || filters.fechaFin) {
        query.createdAt = {};
        if (filters.fechaInicio) {
          const startDate = new Date(filters.fechaInicio + 'T00:00:00');
          query.createdAt.$gte = startDate.toISOString();
        }
        if (filters.fechaFin) {
          const endDate = new Date(filters.fechaFin + 'T23:59:59');
          query.createdAt.$lte = endDate.toISOString();
        }
      }
      
      const { solicitudes: data } = await fetchSolicitudesPrendas({
        limit: ITEMS_LIMIT,
        offset: 1,
        query
      });
      setSolicitudes(data || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error al cargar solicitudes no aprobadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes no aprobadas',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, filters.fechaInicio, filters.fechaFin]);

  useEffect(() => {
    loadSolicitudes();
  }, [loadSolicitudes]);
  
  // Campos de filtro para FilterDrawer
  const filterFields = [
    {
      id: 'fechaInicio',
      label: 'Fecha Inicio',
      type: 'date',
      placeholder: 'Seleccionar fecha inicio'
    },
    {
      id: 'fechaFin',
      label: 'Fecha Fin',
      type: 'date',
      placeholder: 'Seleccionar fecha fin'
    }
  ];
  
  // Contar filtros activos (excluyendo los del rango por defecto)
  const getActiveFiltersCount = () => {
    const defaultRange = getDefaultDateRange();
    let count = 0;
    if (filters.fechaInicio && filters.fechaInicio !== defaultRange.fechaInicio) count++;
    if (filters.fechaFin && filters.fechaFin !== defaultRange.fechaFin) count++;
    return count;
  };
  
  // Manejar cambio de filtros
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };
  
  // Remover filtro individual
  const handleRemoveFilter = (filterId) => {
    const defaultRange = getDefaultDateRange();
    setFilters(prev => ({
      ...prev,
      [filterId]: filterId === 'fechaInicio' ? defaultRange.fechaInicio : defaultRange.fechaFin
    }));
  };
  
  // Limpiar todos los filtros
  const handleClearFilters = () => {
    setFilters(getDefaultDateRange());
  };
  
  // Filtro rápido: Hoy
  const handleTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
      fechaInicio: today,
      fechaFin: today
    });
  };
  
  // Obtener label de rango de fechas para mostrar
  const getDateRangeLabel = () => {
    if (!filters.fechaInicio && !filters.fechaFin) return 'Todas las fechas';
    
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    
    if (filters.fechaInicio === filters.fechaFin) {
      return formatDate(filters.fechaInicio);
    }
    
    return `${formatDate(filters.fechaInicio)} - ${formatDate(filters.fechaFin)}`;
  };

  const filteredSolicitudes = useMemo(() => {
    if (!searchTerm) {
      return [...solicitudes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    const term = searchTerm.toLowerCase();
    return solicitudes
      .filter((sol) => {
        const edificio = sol.edificio?.nombre?.toLowerCase() || '';
        const ciudad = sol.edificio?.ciudad?.toLowerCase() || '';
        const hospedaje = sol.hospedaje?.nombre?.toLowerCase() || sol.nombrePropiedad?.toLowerCase() || '';
        const creator = sol.creator?.nombre?.toLowerCase() || '';
        return (
          edificio.includes(term) ||
          ciudad.includes(term) ||
          hospedaje.includes(term) ||
          creator.includes(term)
        );
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [searchTerm, solicitudes]);

  const columns = useMemo(() => (
    [
      {
        id: 'createdAt',
        label: 'Creada',
        sortable: true,
        render: (row) =>
          new Date(row.createdAt).toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })
      },
      {
        id: 'nombrePropiedad',
        label: 'Portafolio / Propiedad',
        sortable: true,
        render: (row) => (
          <div>
            <p className="font-semibold">{row.edificio?.nombre || row.hospedaje?.edificio?.nombre || '—'}</p>
            <p className="text-sm text-muted-foreground">{row.edificio?.ciudad || row.hospedaje?.edificio?.ciudad || 'Sin ciudad'}</p>
            <p className="text-xs text-purple-600 font-medium">
              🏠 {row.hospedaje?.nombre || row.nombrePropiedad || 'Propiedad sin nombre'}
            </p>
          </div>
        )
      },
      {
        id: 'fechaEntrega',
        label: 'Entrega solicitada',
        sortable: true,
        render: (row) => {
          if (!row.fechaEntrega) {
            return <span className="text-sm text-muted-foreground">—</span>;
          }
          const date = new Date(row.fechaEntrega);
          return (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              {date.toLocaleDateString('es-ES', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </Badge>
          );
        }
      },
      {
        id: 'total_solicitado',
        label: 'Total Solicitado',
        sortable: true,
        render: (row) => (
          <Badge variant="outline" className="bg-purple-50 text-purple-700">
            {row.total_solicitado} u
          </Badge>
        )
      },
      {
        id: 'justificacionMismoDia',
        label: 'Justificación',
        render: (row) => (
          <p className="text-sm text-muted-foreground max-w-xs whitespace-pre-wrap">
            {row.justificacionMismoDia || 'Sin justificación registrada'}
          </p>
        )
      },
      {
        id: 'estado',
        label: 'Estado',
        sortable: true,
        render: () => (
          <Badge className="bg-red-100 text-red-700">No aprobada</Badge>
        )
      },
      {
        id: 'actions',
        label: 'Acciones',
        actions: [
          {
            key: 'ver-detalle-lenceria',
            icon: 'Eye',
            tooltip: 'Ver Detalle',
            className: 'text-purple-600 hover:bg-purple-50'
          },
          {
            key: 'aprobar-solicitud',
            icon: 'Check',
            tooltip: 'Aprobar y mover a Pendiente',
            className: 'text-green-600 hover:bg-green-50',
            showWhen: (row, { approvingId }) => approvingId !== row._id
          }
        ]
      }
    ]
  ), []);

  const handleApproveSolicitud = async (row) => {
    if (!row?._id || approvingId) return;

    const confirmText = `¿Aprobar la solicitud de ${row.hospedaje?.nombre || row.nombrePropiedad || 'esta propiedad'}?`;
    const shouldContinue = window.confirm(confirmText);
    if (!shouldContinue) return;

    setApprovingId(row._id);
    try {
      await updateSolicitudPrendaEstado(row._id, 'Pendiente');
      toast({
        title: 'Solicitud aprobada',
        description: 'El estado cambió a Pendiente y se agregó a la ruta principal.',
      });
      setSolicitudes(prev => prev.filter(sol => sol._id !== row._id));
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error al aprobar solicitud:', error);
      toast({
        title: 'Error al aprobar',
        description: error.message || 'No se pudo actualizar el estado de la solicitud',
        variant: 'destructive',
      });
    } finally {
      setApprovingId(null);
    }
  };

  const handleAction = (actionKey, row) => {
    if (actionKey === 'ver-detalle-lenceria') {
      setSelectedSolicitud(row);
      setIsDetalleModalOpen(true);
    } else if (actionKey === 'aprobar-solicitud') {
      handleApproveSolicitud(row);
    }
  };

  const handleCloseDetalle = () => {
    setSelectedSolicitud(null);
    setIsDetalleModalOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-orange-500" />
            Extra Rutas
          </h1>
          <p className="text-muted-foreground">
            Solicitudes de lencería creadas para entrega el mismo día y pendientes de aprobación.
          </p>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            {getDateRangeLabel()}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={handleTodayFilter}
            className="flex items-center gap-2"
          >
            <CalendarIcon className="h-4 w-4" />
            Hoy
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setIsFilterDrawerOpen(true)}
            className="flex items-center gap-2"
          >
            <FilterIcon className="h-4 w-4" />
            Filtros
            {getActiveFiltersCount() > 0 && (
              <Badge variant="secondary" className="ml-1">
                {getActiveFiltersCount()}
              </Badge>
            )}
          </Button>
          <ExportExcelButton
            service={fetchSolicitudesPrendas}
            filters={{ estado: 'No aprobada', ...filters }}
            columns={[
              { key: 'createdAt', header: 'Fecha Creación', formatDate: true },
              { key: 'fechaEntrega', header: 'Fecha Entrega', formatDate: true },
              { key: 'edificio.nombre', header: 'Portafolio' },
              { key: 'edificio.ciudad', header: 'Ciudad' },
              { key: 'hospedaje.nombre', header: 'Propiedad' },
              { key: 'nombrePropiedad', header: 'Nombre Propiedad' },
              { key: 'total_solicitado', header: 'Total Solicitado' },
              { key: 'justificacionMismoDia', header: 'Justificación' },
              { key: 'estado', header: 'Estado' },
              { key: 'creator.nombre', header: 'Solicitante' }
            ]}
            fileName={`extra-rutas-${new Date().toISOString().split('T')[0]}`}
          />
          <Button variant="outline" onClick={loadSolicitudes} disabled={isLoading} className="flex items-center gap-2">
            <RefreshCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filtros Aplicados */}
      {getActiveFiltersCount() > 0 && (
        <AppliedFilters
          filters={filters}
          fields={filterFields}
          onRemoveFilter={handleRemoveFilter}
        />
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total no aprobadas</p>
                <p className="text-3xl font-bold text-red-600">{filteredSolicitudes.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Solicitudes cargadas</p>
                <p className="text-3xl font-bold text-primary">{solicitudes.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Shirt className="h-6 w-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Límite de consulta: {ITEMS_LIMIT} registros más recientes.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Última actualización</p>
            <p className="text-lg font-semibold">
              {lastUpdated ? lastUpdated.toLocaleString('es-ES', {
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: '2-digit'
              }) : '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Listado de solicitudes no aprobadas</CardTitle>
          <CardDescription>
            Control de solicitudes de lencería que requieren gestión manual.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por portafolio, propiedad o solicitante"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Mostrando {filteredSolicitudes.length} de {solicitudes.length} registros cargados.
            </p>
          </div>

          {filteredSolicitudes.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-lg font-semibold text-foreground">Sin solicitudes pendientes</p>
              <p className="text-sm text-muted-foreground">
                No hay solicitudes de lencería marcadas como "No aprobada" en este momento.
              </p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredSolicitudes}
              isLoading={isLoading}
              onAction={handleAction}
              approvingId={approvingId}
            />
          )}
        </CardContent>
      </Card>

      <DetalleSolicitudModal
        isOpen={isDetalleModalOpen}
        onClose={handleCloseDetalle}
        solicitud={selectedSolicitud}
        tipo="lenceria"
      />
      
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={filters}
        fields={filterFields}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />
    </motion.div>
  );
};

export default ExtraRutasPage;
